import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import FounderDashboard from "@/pages/FounderDashboard";
import Enquiries from "@/pages/Enquiries";
import Children from "@/pages/Children";
import ChildProfile from "@/pages/ChildProfile";
import Fees from "@/pages/Fees";
import Expenses from "@/pages/Expenses";
import Attendance from "@/pages/Attendance";
import DailyUpdates from "@/pages/DailyUpdates";
import PickupDrop from "@/pages/PickupDrop";
import Staff from "@/pages/Staff";
import Reports from "@/pages/Reports";
import ParentPortal from "@/pages/ParentPortal";
import PublicUpdate from "@/pages/PublicUpdate";
import Settings from "@/pages/Settings";
import UserManagement from "@/pages/UserManagement";

function Protected() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function RoleGate({ allow, children }) {
  const { user } = useAuth();
  if (user && !allow.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (user?.role === "parent") return <Navigate to="/parent" replace />;
  if (user?.role === "teacher") return <Navigate to="/attendance" replace />;
  if (user?.role === "front_desk") return <Navigate to="/enquiries" replace />;
  if (user?.role === "founder") return <Navigate to="/founder" replace />;
  return <Dashboard />;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/shared/update/:token" element={<PublicUpdate />} />
            <Route element={<Protected />}>
              <Route element={<Layout />}>
                <Route path="/" element={<HomeRedirect />} />
                <Route path="/founder" element={<RoleGate allow={["founder"]}><FounderDashboard /></RoleGate>} />
                <Route path="/enquiries" element={<RoleGate allow={["admin", "front_desk"]}><Enquiries /></RoleGate>} />
                <Route path="/children" element={<Children />} />
                <Route path="/children/:id" element={<ChildProfile />} />
                <Route path="/expenses" element={<RoleGate allow={["admin", "founder"]}><Expenses /></RoleGate>} />
                <Route path="/fees" element={<RoleGate allow={["admin", "front_desk"]}><Fees /></RoleGate>} />
                <Route path="/attendance" element={<RoleGate allow={["admin", "teacher", "front_desk"]}><Attendance /></RoleGate>} />
                <Route path="/daily-updates" element={<RoleGate allow={["admin", "teacher", "parent"]}><DailyUpdates /></RoleGate>} />
                <Route path="/pickup-drop" element={<RoleGate allow={["admin", "front_desk", "teacher"]}><PickupDrop /></RoleGate>} />
                <Route path="/staff" element={<RoleGate allow={["admin", "front_desk"]}><Staff /></RoleGate>} />
                <Route path="/reports" element={<RoleGate allow={["admin"]}><Reports /></RoleGate>} />
                <Route path="/user-management" element={<RoleGate allow={["admin"]}><UserManagement /></RoleGate>} />
              <Route path="/settings" element={<RoleGate allow={["admin"]}><Settings /></RoleGate>} />
                <Route path="/parent" element={<RoleGate allow={["parent", "admin"]}><ParentPortal /></RoleGate>} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
